import * as i0 from '@angular/core';
import { Injector, Injectable, ViewContainerRef, Component, ViewChild, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReplaySubject } from 'rxjs';
import * as i1 from '@dch/ng-common';

var ModalMessageType;
(function (ModalMessageType) {
    ModalMessageType[ModalMessageType["Success"] = 'success'] = "Success";
    ModalMessageType[ModalMessageType["Warning"] = 'warning'] = "Warning";
    ModalMessageType[ModalMessageType["Error"] = 'error'] = "Error";
})(ModalMessageType || (ModalMessageType = {}));
var ModalType;
(function (ModalType) {
    ModalType[ModalType["MessageBox"] = 'messageBox'] = "MessageBox";
    ModalType[ModalType["Overlayer"] = 'overlayer'] = "Overlayer";
})(ModalType || (ModalType = {}));
class ModalService {
    constructor(componentFactoryResolver, inceptionService) {
        this.componentFactoryResolver = componentFactoryResolver;
        this.inceptionService = inceptionService;
        this.modalPlaceholderSelector = 'modal-placeholder';
        this.modalSelector = '#px-msg-overlayer';
        // we can use this to determine z-index of multiple modals
        this.activeInstances = 0;
    }
    registerViewContainerRef(vcRef) {
        this.vcRef = vcRef;
    }
    registerInjector(injector) {
        this.injector = injector;
    }
    create(component, parameters, messageType, modalType, isNegative) {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
        const componentRef$ = new ReplaySubject();
        const childInjector = Injector.create({
            providers: [],
            parent: this.injector,
        });
        const componentRef = this.vcRef.createComponent(componentFactory, 0, childInjector);
        // pass the @Input parameters to the instance
        Object.assign(componentRef.instance, parameters);
        this.activeInstances++;
        componentRef.instance['destroy'] = () => {
            this.activeInstances--;
            setTimeout(() => Foundation.libs.reveal.close());
            // We use a 800ms timeout to wait for the close modal animation to complete
            setTimeout(() => {
                componentRef.destroy();
            }, 800);
        };
        this.initFoundationAndReveal(messageType, modalType, isNegative);
        componentRef$.next(componentRef);
        componentRef$.complete();
        // destroy existing component
        if (this.activeComponent) {
            this.activeComponent.destroy();
        }
        this.activeComponent = componentRef;
        try {
            this.inceptionService.focustrap('#px-msg-overlayer');
        }
        catch (e) {
            console.log(e);
        }
        return componentRef$.asObservable();
    }
    initFoundationAndReveal(messageType, modalType, isNegative) {
        const el = jQuery(this.modalSelector);
        Foundation.libs.reveal.init(this.modalPlaceholderSelector);
        if (modalType === ModalType.MessageBox) {
            el.addClass('rs-reveal-msgbox');
            const messageBoxClass = this.getMessageBoxClass(messageType);
            el.addClass(messageBoxClass);
            if (el.removeClass) {
                el.removeClass(function (index, className) {
                    return (className.match(/(^|\s)rs-reveal-modal-neg/g) || []).join(' ');
                });
            }
        }
        else {
            el.addClass('rs-reveal-modal');
            if (el.removeClass) {
                el.removeClass(function (index, className) {
                    return (className.match(/(^|\s)rs-reveal-msg\S+|rs-reveal-modal-neg/g) || []).join(' ');
                });
            }
        }
        if (isNegative) {
            el.addClass('rs-reveal-modal-neg');
        }
        // Note: Initialising foundation on the element before calling any foundation functions.
        // https://github.com/foundation/foundation-sites/issues/3702#issuecomment-39249038
        // fix for "Cannot read property css of undefined" issue linked to foundation reveal v5.2 (used in PROD).
        el.foundation().foundation('reveal', 'open');
    }
    getMessageBoxClass(messageType) {
        switch (messageType) {
            case ModalMessageType.Success:
                return 'rs-reveal-msgbox-success';
            case ModalMessageType.Error:
                return 'rs-reveal-msgbox-error';
            case ModalMessageType.Warning:
                return 'rs-reveal-msgbox-warning';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalService, deps: [{ token: i0.ComponentFactoryResolver }, { token: i1.InceptionService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i1.InceptionService }] });

class PxModalPlaceholderComponent {
    constructor(modalService, injector) {
        this.modalService = modalService;
        this.injector = injector;
    }
    ngOnInit() {
        this.init();
    }
    ngAfterViewInit() {
        this.init();
    }
    init() {
        this.modalService.registerViewContainerRef(this.viewContainerRef);
        this.modalService.registerInjector(this.injector);
    }
    close() {
        // We use a 800ms timeout to wait for the close modal animation to complete -
        // we use this because we can't get the component reference from viewContainerRef without knowing it's type
        setTimeout(() => {
            this.viewContainerRef.remove();
        }, 800);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: PxModalPlaceholderComponent, deps: [{ token: ModalService }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.5", type: PxModalPlaceholderComponent, isStandalone: false, selector: "px-modal-placeholder", viewQueries: [{ propertyName: "viewContainerRef", first: true, predicate: ["modalplaceholder"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: "<span\n  data-reveal=\"\"\n  id=\"px-msg-overlayer\"\n  role=\"dialog\"\n  aria-labelledby=\"ac-msg-overlayer-label\"\n  class=\"reveal-modal rs-reveal-modal-scroll\"\n>\n  <div #modalplaceholder></div>\n</span>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: PxModalPlaceholderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'px-modal-placeholder', standalone: false, template: "<span\n  data-reveal=\"\"\n  id=\"px-msg-overlayer\"\n  role=\"dialog\"\n  aria-labelledby=\"ac-msg-overlayer-label\"\n  class=\"reveal-modal rs-reveal-modal-scroll\"\n>\n  <div #modalplaceholder></div>\n</span>\n" }]
        }], ctorParameters: () => [{ type: ModalService }, { type: i0.Injector }], propDecorators: { viewContainerRef: [{
                type: ViewChild,
                args: ['modalplaceholder', { read: ViewContainerRef }]
            }] } });

class ModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.0.5", ngImport: i0, type: ModalModule, declarations: [PxModalPlaceholderComponent], imports: [CommonModule], exports: [PxModalPlaceholderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.5", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [PxModalPlaceholderComponent],
                    exports: [PxModalPlaceholderComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ModalMessageType, ModalModule, ModalService, ModalType, PxModalPlaceholderComponent };
